import type {
  NotificationApi,
  NotificationCategory,
  NotificationPublishInput,
  NotificationRecord,
  NotificationSettings,
  NotificationSubscriber,
  NotificationTransportEvent,
  NotificationTransportMode,
} from '@/notifications/NotificationTypes';

function nowIso(): string {
  return new Date().toISOString();
}

function buildNotification(
  input: NotificationPublishInput,
): NotificationRecord {
  const createdAt = nowIso();
  const id = input.id ?? `${createdAt}-${Math.random().toString(16).slice(2)}`;

  return {
    id,
    title: input.title,
    message: input.message,
    productId: input.productId,
    productName: input.productName,
    productIcon: input.productIcon,
    category: input.category ?? 'application',
    type: input.type ?? 'productEvent',
    severity: input.severity ?? 'info',
    status: 'unread',
    createdAt,
    createdBy: input.createdBy,
    actionUrl: input.actionUrl,
    metadata: input.metadata,
    read: false,
    archived: false,
    deepLink: input.deepLink,
  };
}

export class NotificationService {
  private readonly subscribers = new Set<NotificationSubscriber>();

  private readonly store = new Map<string, NotificationRecord>();

  private mode: NotificationTransportMode = 'rest';

  private isConnected = false;

  private readonly api: NotificationApi = {
    getNotifications: async () => this.listSorted(),
    getUnreadCount: async () => this.computeUnreadCount(),
    markRead: async (ids) => {
      this.patchMany(ids, { read: true, status: 'read' });
    },
    markUnread: async (ids) => {
      this.patchMany(ids, { read: false, status: 'unread', archived: false });
    },
    archive: async (ids) => {
      this.patchMany(ids, { archived: true, status: 'archived', read: true });
    },
    deleteById: async (id) => {
      this.store.delete(id);
    },
    getCategories: async () => [
      'system',
      'application',
      'security',
      'audit',
      'userActivity',
      'featureFlags',
    ],
    getSettings: async (): Promise<NotificationSettings> => ({
      realtimeEnabled: true,
      desktopEnabled: false,
      emailDigestEnabled: false,
    }),
  };

  async connect(mode: NotificationTransportMode = 'sse'): Promise<void> {
    this.mode = mode;
    this.isConnected = true;
  }

  async disconnect(): Promise<void> {
    this.isConnected = false;
  }

  subscribe(handler: NotificationSubscriber): void {
    this.subscribers.add(handler);
  }

  unsubscribe(handler: NotificationSubscriber): void {
    this.subscribers.delete(handler);
  }

  async publish(input: NotificationPublishInput): Promise<NotificationRecord> {
    const notification = buildNotification(input);

    if (this.store.has(notification.id)) {
      return this.store.get(notification.id) as NotificationRecord;
    }

    this.store.set(notification.id, notification);
    this.emit({ type: 'created', notification });

    return notification;
  }

  async acknowledge(notificationId: string): Promise<void> {
    await this.api.markRead([notificationId]);

    const current = this.store.get(notificationId);

    if (current) {
      this.emit({
        type: 'updated',
        notification: {
          ...current,
          read: true,
          status: 'read',
        },
      });
    }
  }

  getApi(): NotificationApi {
    return this.api;
  }

  getTransportMode(): NotificationTransportMode {
    return this.mode;
  }

  getConnected(): boolean {
    return this.isConnected;
  }

  private emit(event: NotificationTransportEvent): void {
    this.subscribers.forEach((subscriber) => subscriber(event));
  }

  private patchMany(
    ids: string[],
    updates: Partial<Pick<NotificationRecord, 'read' | 'status' | 'archived'>>,
  ) {
    ids.forEach((id) => {
      const current = this.store.get(id);

      if (!current) {
        return;
      }

      const next: NotificationRecord = {
        ...current,
        ...updates,
      };

      this.store.set(id, next);
      this.emit({ type: 'updated', notification: next });
    });
  }

  private computeUnreadCount(): number {
    return this.listSorted().filter((notification) => !notification.read)
      .length;
  }

  private listSorted(): NotificationRecord[] {
    return Array.from(this.store.values()).sort((a, b) => {
      return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
    });
  }
}

export const notificationService = new NotificationService();

export async function getNotifications(): Promise<NotificationRecord[]> {
  return notificationService.getApi().getNotifications();
}

export async function getUnreadNotificationCount(): Promise<number> {
  return notificationService.getApi().getUnreadCount();
}

export async function markNotificationsRead(ids: string[]): Promise<void> {
  return notificationService.getApi().markRead(ids);
}

export async function markNotificationsUnread(ids: string[]): Promise<void> {
  return notificationService.getApi().markUnread(ids);
}

export async function archiveNotifications(ids: string[]): Promise<void> {
  return notificationService.getApi().archive(ids);
}

export async function deleteNotification(id: string): Promise<void> {
  return notificationService.getApi().deleteById(id);
}

export async function getNotificationCategories(): Promise<
  NotificationCategory[]
> {
  return notificationService.getApi().getCategories();
}

export async function getNotificationSettings(): Promise<NotificationSettings> {
  return notificationService.getApi().getSettings();
}
